'use client';

import React, { createContext, useContext } from 'react';

// 使用 undefined 作为默认值，这样可以更好地检测是否在Provider内部
const LocaleContext = createContext<{ locale: string } | undefined>(undefined);

export function LocaleProvider({
    children,
    locale,
}: Readonly<{
    children: React.ReactNode;
    locale: string;
}>) {
    const providerValue = React.useMemo(() => ({ locale }), [locale]);

    return (
        <LocaleContext.Provider value={providerValue}>
            {children}
        </LocaleContext.Provider>
    );
}

export function useLocale() {
    // 在构建阶段完全避免 useContext，直接返回默认值
    if (typeof window === 'undefined') {
        // 检查是否在构建过程中
        if (
            process.env.NODE_ENV === 'production' ||
            process.env.NEXT_PHASE === 'phase-production-build'
        ) {
            return 'en';
        }
    }

    try {
        const context = useContext(LocaleContext);
        if (context === undefined) {
            // 在构建时或错误页面预渲染时，提供默认语言而不抛出错误
            if (typeof window === 'undefined') {
                // 服务端渲染时使用默认语言
                return 'en';
            }
            // 在客户端但没有 Provider 时，也提供默认值而不抛出错误
            console.warn(
                'useLocale is being used outside of LocaleProvider, falling back to default locale',
            );
            return 'en';
        }
        return context.locale;
    } catch (error) {
        // 如果 React Context 系统本身出错，提供默认值
        console.warn(
            'Error accessing LocaleContext, falling back to default locale:',
            error,
        );
        return 'en';
    }
}
